import React from 'react';
import { Search, FolderSearch, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { ToolCallDisplayProps } from './types';

const getToolIcon = (toolName: string): React.ReactNode => {
  switch (toolName.toLowerCase()) {
    case 'browse':
      return <FolderSearch size={14} />;
    case 'get_analysis':
      return <FileText size={14} />;
    default:
      return <Search size={14} />;
  }
};

const getToolDisplayName = (toolName: string): string => {
  switch (toolName.toLowerCase()) {
    case 'search':
      return 'Searching media';
    case 'get_analysis':
      return 'Reading analysis';
    case 'browse':
      return 'Browsing library';
    default:
      return toolName.replace(/_/g, ' ');
  }
};

const getResultSummary = (result: Record<string, any> | undefined): string | null => {
  if (!result) return null;

  const count = result.results?.included_results ?? result.results?.included_videos ?? (Array.isArray(result.entries) ? result.entries.length : undefined);
  if (typeof count === 'number') return `${count} result${count === 1 ? '' : 's'}`;

  // Check for segments count
  if (result.segments_count !== undefined) {
    return `${result.segments_count} result${result.segments_count !== 1 ? 's' : ''}`;
  }

  if (result.count !== undefined) {
    return `${result.count} result${result.count !== 1 ? 's' : ''}`;
  }

  // Check for results array
  if (Array.isArray(result.results)) {
    return `${result.results.length} result${result.results.length !== 1 ? 's' : ''}`;
  }

  // Check for segments array
  if (Array.isArray(result.segments)) {
    return `${result.segments.length} segment${result.segments.length !== 1 ? 's' : ''}`;
  }

  return null;
};

export const ToolCallDisplay: React.FC<ToolCallDisplayProps> = ({ toolCall }) => {
  const { toolName, status, resultSummary, error } = toolCall;
  const isLoading = status === 'started';
  const isComplete = status === 'completed';
  const isError = status === 'error';

  const summaryText = getResultSummary(resultSummary);

  return (
    <div className={`tool-call-display ${status}`}>
      <div className="tool-call-icon">
        {isLoading ? (
          <Loader2 size={14} className="spinning" />
        ) : isComplete ? (
          <CheckCircle size={14} className="tool-success" />
        ) : isError ? (
          <AlertCircle size={14} className="tool-error" />
        ) : (
          getToolIcon(toolName)
        )}
      </div>
      <div className="tool-call-info">
        <span className="tool-call-name">{getToolDisplayName(toolName)}</span>
        {isLoading && <span className="tool-call-status">Running</span>}
        {isComplete && summaryText && (
          <span className="tool-call-result">{summaryText}</span>
        )}
        {isError && error && (
          <span className="tool-call-result">{error}</span>
        )}
      </div>
    </div>
  );
};

export default ToolCallDisplay;
